import { useMemo } from 'react';
import { BaseApiClient } from '../api/api.generated';
import { Config } from '../config';
import { createAuthClientFetch } from '../services/auth-client/createAuthClientFetch';
import { useCountries } from './useCountries';

/**
 * Hook to use any generated api client with locale and authorization headers set.
 *
 * @example Create a {@link PartyBookingApiClient}
 * ```ts
 * const partyBookingApiClient = useApiClient(PartyBookingApiClient);
 * ```
 *
 * @param apiClientType The type of api client to return, e.g. PartyBookingApiClient.
 */
export const useApiClient = <T extends BaseApiClient>(apiClientType: {
    new (baseUrl: string, http?: { fetch(url: RequestInfo, init?: RequestInit): Promise<Response> }): T;
}): T => {
    const { currentCountry } = useCountries();

    return useMemo(() => {
        // The auth client fetch resolves the cookie session's short-lived access token per request and sets the
        // authorization header from it.
        const client = new apiClientType(Config.baseUrlApi, createAuthClientFetch());

        client.locale = `${currentCountry.language}-${currentCountry.code}`;

        return client;
    }, [apiClientType, currentCountry]);
};
